import { Avatar } from '@synoptic/ui-kit/avatar/avatar.tsx';
import { Button } from '@synoptic/ui-kit/button/button.js';
import { CameraIcon } from '@synoptic/ui-kit/icons/react/camera.tsx';
import { Input } from '@synoptic/ui-kit/input/input.tsx';
import { useIsTablet } from '@synoptic/ui-kit/responsive/hooks.ts';
import { Textarea } from '@synoptic/ui-kit/textarea/textarea.js';
import { borderRadius, space } from '@synoptic/ui-kit/tokens.js';
import { typography } from '@synoptic/ui-kit/typography.js';
import { clsx } from 'clsx';
import React, { useRef } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { S3ObjectInput } from '@/__generated__/graphql.ts';
import { CoverImage } from '@/components/cover-image/cover-image.tsx';
import { getUserS3Url } from '@/components/s3/user/get-user-s3-url.ts';
import {
  avatarBox,
  updateImageBackgroundImg,
} from '@/components/update-user-image/update-image-modal.css.ts';
import { UpdateImageModal } from '@/components/update-user-image/update-image-modal.tsx';
import { useUpdateAvatar } from '@/components/update-user-image/use-update-avatar.ts';
import { useUpdateBackgroundImage } from '@/components/update-user-image/use-update-background.ts';
import {
  coverImageButtonContainer,
  coverImageContainerBorderRadius,
  editProfileAction,
  editProfileActionLabel,
  editProfileActionLabelDescription,
  editProfileAvatarActionButtons,
  editProfileAvatarContainer,
  editProfileBgContainer,
  editProfileForm,
  editProfileInputsContainer,
  editProfileSubmitButton,
} from '@/routes/_app.$username/profile-card/main-statistics/identity/edit-profile/edit-profile-form/edit-profile-form.css.ts';
import { coverImage } from '@/routes/_app.$username/profile-card/profile-card.css.ts';
import { useMe } from '@/routes/_app/use-me.ts';

type FormInputs = {
  fullName: string;
  bio?: string;
};

export type EditProfileFormProps = {
  onSubmit: (input: { fullName: string; bio?: string }) => void;
  titleText?: string;
  backgroundImageKey?: string;
};

export const EditProfileForm: React.FC<EditProfileFormProps> = ({
  onSubmit,
  backgroundImageKey,
}) => {
  const me = useMe();
  const isTablet = useIsTablet();
  const { handleSubmit, register, formState } = useForm<FormInputs>({
    defaultValues: { fullName: me.fullName, bio: me?.bio ?? '' },
  });

  const formRef = useRef<HTMLFormElement>(null);

  const submit: SubmitHandler<FormInputs> = async (data) => {
    onSubmit({
      fullName: data.fullName,
      bio: data.bio,
    });
  };

  const avatarUrl = getUserS3Url(me.id, me.avatar?.key);
  const [updateAvatar] = useUpdateAvatar();
  const onAvatarUpdate = (image: S3ObjectInput) => {
    updateAvatar({ avatar: image });
  };
  const removeAvatarImage = () => {
    updateAvatar({ avatar: null });
  };
  const bgUrl = getUserS3Url(me.id, backgroundImageKey);
  const [updateBackgroundImage] = useUpdateBackgroundImage();
  const onBgImageUpdate = (image: S3ObjectInput) => {
    updateBackgroundImage({ backgroundImage: image });
  };
  const removeBgImage = () => {
    updateBackgroundImage({ backgroundImage: null });
  };

  return (
    <div className={editProfileForm}>
      {!isTablet && (
        <div style={{ position: 'relative' }}>
          <div
            style={{ position: 'absolute', right: 19, bottom: 16 }}
            className={editProfileAvatarActionButtons}
          >
            <UpdateImageModal
              trigger={
                <Button
                  variant="outline"
                  size={'small'}
                  endIcon={<CameraIcon />}
                  className={coverImageButtonContainer}
                >
                  Edit cover
                </Button>
              }
              onUpdate={onBgImageUpdate}
              mainTextEmpty={'Pick a background image'}
              subTextEmpty={'Recommended size: 1000x250 pixels.'}
              mainText={'Looking good!'}
              subText={
                "You're almost set! Make sure your new background image looks great before finalizing it."
              }
              editorProps={{
                width: isTablet ? 600 : 300,
                height: isTablet ? 150 : 75,
                borderRadius: borderRadius.r16,
                cropperPadding: 15,
              }}
              renderPreview={(previewSrc) => (
                <CoverImage
                  src={previewSrc || bgUrl?.toString()}
                  className={updateImageBackgroundImg}
                />
              )}
              previewBorderRadius={borderRadius.r16}
            />
            <Button
              variant="outline"
              size={'small'}
              className={coverImageButtonContainer}
              onClick={removeBgImage}
              disabled={!backgroundImageKey}
            >
              Remove
            </Button>
          </div>
          <CoverImage
            s3Key={backgroundImageKey}
            userId={me.id}
            className={clsx(coverImage)}
          />
        </div>
      )}

      <div className={editProfileAvatarContainer}>
        <div>
          <Avatar size={'xl'} imageSrc={avatarUrl?.toString()} seed={me.id} />
        </div>

        <div className={editProfileAction}>
          <div>
            <span className={editProfileActionLabel}>Profile picture</span>
            <span className={editProfileActionLabelDescription}>
              Recommended 400 x 400 px
            </span>
          </div>
          <div className={editProfileAvatarActionButtons}>
            <UpdateImageModal
              trigger={
                <Button variant={'secondary'} size={'small'}>
                  Edit picture
                </Button>
              }
              onUpdate={onAvatarUpdate}
              mainTextEmpty={'Pick a profile picture'}
              subTextEmpty={'Recommended size: 400x400 pixels.'}
              mainText={'Looking good!'}
              subText={
                "You're almost set! Make sure your new avatar looks great before finalizing it."
              }
              editorProps={{
                width: 350,
                height: 350,
              }}
              className={avatarBox}
              renderPreview={(previewSrc) => (
                <Avatar
                  size={'xl'}
                  imageSrc={previewSrc || avatarUrl?.toString()}
                  seed={me.id}
                />
              )}
              previewBorderRadius={9999}
            />
            <Button
              variant={'secondary'}
              size={'small'}
              onClick={removeAvatarImage}
              disabled={!me.avatar?.key}
            >
              Remove
            </Button>
          </div>
        </div>
      </div>

      {isTablet && (
        <div className={editProfileBgContainer}>
          <div className={editProfileAction}>
            <div>
              <span className={editProfileActionLabel}>Cover picture</span>
              <span className={editProfileActionLabelDescription}>
                Recommended 1500 x 500 px
              </span>
            </div>
            <div className={editProfileAvatarActionButtons}>
              <UpdateImageModal
                trigger={
                  <Button variant={'secondary'} size={'small'}>
                    Edit cover
                  </Button>
                }
                onUpdate={onBgImageUpdate}
                mainTextEmpty={'Pick a cover picture'}
                subTextEmpty={'Recommended size: 1500x500 pixels.'}
                mainText={'Looking good!'}
                subText={
                  "You're almost set! Make sure your new background image looks great before finalizing it."
                }
                editorProps={{
                  width: isTablet ? 600 : 300,
                  height: isTablet ? 150 : 75,
                  borderRadius: borderRadius.r16,
                  cropperPadding: 15,
                }}
                renderPreview={(previewSrc) => (
                  <CoverImage
                    src={previewSrc || bgUrl?.toString()}
                    className={updateImageBackgroundImg}
                  />
                )}
                previewBorderRadius={borderRadius.r16}
              />
              <Button
                variant={'secondary'}
                size={'small'}
                onClick={removeBgImage}
                disabled={!backgroundImageKey}
              >
                Remove
              </Button>
            </div>
            {backgroundImageKey && (
              <CoverImage
                s3Key={backgroundImageKey}
                userId={me.id}
                className={clsx(coverImage, coverImageContainerBorderRadius)}
              />
            )}
          </div>
        </div>
      )}

      <form ref={formRef} onSubmit={handleSubmit(submit)}>
        <div className={editProfileInputsContainer}>
          <Input
            autoFocus
            {...register('fullName', {
              required: 'Name is required',
              maxLength: {
                value: 50,
                message: 'Name cannot exceed 50 characters',
              },
              validate: (v) => (v ? v.trim().length > 0 : undefined),
            })}
            defaultValue={formState.defaultValues?.fullName}
            maxLength={50}
            errorMessage={formState.errors.fullName?.message}
            placeholder={'Enter the name here'}
            label={'Name (required)'}
          />

          <Input
            autoFocus
            value={'@' + me.username}
            disabled={true}
            label={'Username (required)'}
          />

          <Textarea
            {...register('bio', {
              required: false,
              maxLength: {
                value: 160,
                message: 'Bio cannot exceed 160 characters',
              },
              validate: (v) => (v ? v.trim().length > 0 : undefined),
            })}
            errorMessage={formState.errors.bio?.message}
            placeholder={'Enter the bio here'}
            style={{
              ...typography.bodyMd,
              padding: `${space.s12}px ${space.s16}px`,
              borderRadius: borderRadius.r8,
              minHeight: 110,
            }}
            label={'Bio (required)'}
          />
        </div>

        <Button
          type="submit"
          size={'large'}
          className={editProfileSubmitButton}
        >
          Save
        </Button>
      </form>
    </div>
  );
};
